import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Z } from '../../../styles/zLayers';
import LetterSpacingInput from './LetterSpacingInput';
import LineHeightInput from './LineHeightInput';
import { useExclusivePopover } from './PopoverGroup';

export type TextCase = 'none' | 'uppercase' | 'lowercase' | 'titlecase';

export interface TextStyleValue {
  bold: boolean;
  italic: boolean;
  underline: boolean;
  strikethrough: boolean;
  /** Drop shadow behind the text (defaults ON — the legacy publish look). */
  shadow: boolean;
  textCase: TextCase;
  /** Letter spacing as integer percent (0 = default). */
  letterSpacing: number;
  /** Unitless line-height multiplier; null = Auto (font default). */
  lineHeight: number | null;
}

interface TextStylePopoverProps {
  value: TextStyleValue;
  onChange: (next: TextStyleValue) => void;
  /** Seed shown when stepping the line-height control from Auto. */
  lineHeightAutoSeed?: number;
  /** Auto = pick above/below based on viewport. */
  position?: 'above' | 'below' | 'auto';
  disabled?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
}

const POPOVER_W = 180;
const POPOVER_H = 178;

type PopoverCoords = { top?: number; bottom?: number; left: number };

const TextStylePopover: React.FC<TextStylePopoverProps> = ({
  value,
  onChange,
  lineHeightAutoSeed = 1.25,
  position = 'above',
  disabled = false,
  onOpenChange,
}) => {
  const [isOpen, setIsOpen] = useExclusivePopover();
  const [coords, setCoords] = useState<PopoverCoords | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => { onOpenChange?.(isOpen); }, [isOpen, onOpenChange]);

  const calc = useCallback((): PopoverCoords | null => {
    if (!triggerRef.current) return null;
    const r = triggerRef.current.getBoundingClientRect();
    // Anchor to the toolbar pill (parent), not just the trigger button — that
    // way the popover sits flush against the visual edge of the toolbar with
    // zero gap, even with the pill's vertical padding.
    const parent = triggerRef.current.parentElement;
    const anchor = parent ? parent.getBoundingClientRect() : r;
    const goAbove = position === 'above' || (position === 'auto' && anchor.top > POPOVER_H + 10);
    // For "above": anchor by the popover's BOTTOM so it sits flush against
    // the toolbar's top edge regardless of the popover's actual content
    // height. For "below": anchor by the top.
    // Left-align with the trigger button so the popover visually connects to
    // the toolbar control it expands from.
    if (goAbove) {
      return {
        bottom: window.innerHeight - anchor.top,
        left: r.left,
      };
    }
    return {
      top: anchor.bottom,
      left: r.left,
    };
  }, [position]);

  useEffect(() => {
    if (!isOpen) return;
    setCoords(calc());
    const onScroll = () => setCoords(calc());
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [isOpen, calc]);

  useEffect(() => {
    if (!isOpen) return;
    const onClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (popoverRef.current?.contains(t) || triggerRef.current?.contains(t)) return;
      setIsOpen(false);
    };
    // Defer listener install so the same click that opened us doesn't close us.
    const tid = setTimeout(() => document.addEventListener('mousedown', onClick), 10);
    return () => {
      clearTimeout(tid);
      document.removeEventListener('mousedown', onClick);
    };
  }, [isOpen, setIsOpen]);

  const setEmphasis = (key: 'bold' | 'italic' | 'underline' | 'strikethrough') =>
    onChange({ ...value, [key]: !value[key] });

  const setCase = (next: TextCase) =>
    onChange({ ...value, textCase: value.textCase === next ? 'none' : next });

  const Btn: React.FC<{ active: boolean; onClick: () => void; label: string; aria: string; style?: React.CSSProperties }>
    = ({ active, onClick, label, aria, style }) => (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      aria-label={aria}
      aria-pressed={active}
      className={`w-6 h-6 text-[11px] rounded border transition-colors ${active
        ? 'bg-blue-500 border-blue-400 text-white'
        : 'bg-black/40 border-white/20 text-white/80 hover:bg-black/60'}`}
      style={style}
    >
      {label}
    </button>
  );

  // Shadow counts as "active" only when OFF — it deviates from the default.
  const anyActive = value.bold || value.italic || value.underline || value.strikethrough
    || !value.shadow
    || value.textCase !== 'none' || value.letterSpacing !== 0 || value.lineHeight != null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (!disabled) setIsOpen(!isOpen); }}
        disabled={disabled}
        title="Text style"
        aria-label="Text style options"
        className={`w-6 h-6 flex items-center justify-center rounded border transition-colors ${anyActive
          ? 'bg-blue-500 border-blue-400 text-white'
          : 'bg-black/40 border-white/20 text-white/80 hover:bg-black/60'}`}
      >
        {/* Bold "T" with underline — represents text styling. */}
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
          <path d="M6 6h12" strokeWidth="3" />
          <path d="M12 6v11" strokeWidth="3" />
          <path d="M5 21h14" strokeWidth="2" />
        </svg>
      </button>
      {isOpen && coords && createPortal(
        <div
          ref={popoverRef}
          data-overlay-popover=""
          onMouseDown={(e) => e.stopPropagation()}
          style={{
            position: 'fixed',
            top: coords.top,
            bottom: coords.bottom,
            left: coords.left,
            width: POPOVER_W,
            zIndex: Z.PICKER,
          }}
          className="bg-zinc-900/95 backdrop-blur rounded-md border border-white/15 shadow-xl px-2 py-1.5 flex flex-col gap-1"
        >
          <div className="grid grid-cols-[3rem_1fr] items-center gap-1.5">
            <span className="text-[9px] uppercase tracking-wide text-white/60">Style</span>
            <div className="grid grid-cols-4 gap-0.5">
              <Btn active={value.bold} onClick={() => setEmphasis('bold')} label="B" aria="Bold" style={{ fontWeight: 700 }} />
              <Btn active={value.italic} onClick={() => setEmphasis('italic')} label="I" aria="Italic" style={{ fontStyle: 'italic' }} />
              <Btn active={value.underline} onClick={() => setEmphasis('underline')} label="U" aria="Underline" style={{ textDecoration: 'underline' }} />
              <Btn active={value.strikethrough} onClick={() => setEmphasis('strikethrough')} label="S" aria="Strikethrough" style={{ textDecoration: 'line-through' }} />
            </div>
          </div>
          <div className="h-px bg-white/10" />
          <div className="grid grid-cols-[3rem_1fr] items-center gap-1.5">
            <span className="text-[9px] uppercase tracking-wide text-white/60">Case</span>
            <div className="grid grid-cols-4 gap-0.5">
              <Btn active={value.textCase === 'uppercase'} onClick={() => setCase('uppercase')} label="TD" aria="ALL CAPS" />
              <Btn active={value.textCase === 'lowercase'} onClick={() => setCase('lowercase')} label="td" aria="lowercase" />
              <Btn active={value.textCase === 'titlecase'} onClick={() => setCase('titlecase')} label="Td" aria="Title Case" />
            </div>
          </div>
          <div className="h-px bg-white/10" />
          <div className="grid grid-cols-[3rem_1fr] items-center gap-1.5">
            <span className="text-[9px] uppercase tracking-wide text-white/60">Shadow</span>
            <div className="grid grid-cols-4 gap-0.5">
              <Btn
                active={value.shadow}
                onClick={() => onChange({ ...value, shadow: !value.shadow })}
                label="S"
                aria="Text shadow"
                style={{ textShadow: '0 1px 2px rgba(0, 0, 0, 0.9)' }}
              />
            </div>
          </div>
          <div className="h-px bg-white/10" />
          <div className="grid grid-cols-[3rem_1fr] items-center gap-1.5">
            <span className="text-[9px] uppercase tracking-wide text-white/60">Letter</span>
            <LetterSpacingInput
              value={value.letterSpacing}
              onChange={(letterSpacing) => onChange({ ...value, letterSpacing })}
              disabled={disabled}
            />
          </div>
          <div className="grid grid-cols-[3rem_1fr] items-center gap-1.5">
            <span className="text-[9px] uppercase tracking-wide text-white/60">Line</span>
            <LineHeightInput
              value={value.lineHeight}
              onChange={(lineHeight) => onChange({ ...value, lineHeight })}
              autoSeed={lineHeightAutoSeed}
              disabled={disabled}
            />
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};

export default TextStylePopover;
